import { useEffect, useRef } from "react";
import changeSound from "../media/change.mp3";
import countdownSound from "../media/countdown.mp3";

// iOS Safari only allows an <audio> element to play without a fresh user
// gesture once that *exact* element has already played during a real
// gesture earlier in the page's lifetime - a new Audio() created at warning
// time is always "locked" there, silently swallowing play(). So each sound
// gets a single, page-lifetime element (below) that a real tap unlocks
// once (see useAudioUnlock), and every later timer-driven play() reuses it.
const audioElements = {};

function getAudioElement(src) {
  if (!audioElements[src]) {
    audioElements[src] = new Audio(src);
  }
  return audioElements[src];
}

function playSound(src) {
  try {
    const audio = getAudioElement(src);
    audio.currentTime = 0;
    audio.play().catch(() => {
      // Playback can be blocked (autoplay policy, no user interaction yet) -
      // there is nothing useful to do about that, so just skip the sound.
    });
  } catch {
    // Audio unsupported in this environment - skip the sound.
  }
}

let audioUnlocked = false;

function unlockAudioElements() {
  if (audioUnlocked) {
    return;
  }
  audioUnlocked = true;
  [changeSound, countdownSound].forEach((src) => {
    const audio = getAudioElement(src);
    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
      })
      .catch(() => {
        // Still locked (e.g. this "gesture" wasn't one iOS accepts) - the
        // next real tap will retry since audioUnlocked resets below.
        audioUnlocked = false;
      });
  });
}

// The very first tap/click/keypress anywhere on the page satisfies iOS's
// "user gesture" requirement for both sounds at once, well before either
// would actually need to play (the user has to interact to set up cards
// before any timer sound is due).
function useAudioUnlock() {
  useEffect(() => {
    const events = ["pointerdown", "touchend", "keydown"];
    events.forEach((event) =>
      document.addEventListener(event, unlockAudioElements, {
        once: true,
        passive: true,
      })
    );
    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, unlockAudioElements)
      );
    };
  }, []);
}

// Plays the "card changed" and "time's almost up" sounds for the running
// timer. Everything here is driven by comparing this tick's state to the
// previous one via refs, so:
//  - loading/reloading the page mid-session never fires a sound for
//    whatever was already happening before this hook existed for that page
//  - the warning for a card fires exactly once, not on every tick while its
//    remaining time stays under the threshold
//  - a card too short for the configured warning lead time (it would
//    otherwise fire almost at the card's start) gets a capped warning
//    instead of a nonsensical one, by capping the lead time at half the
//    card's own duration
//  - clicking Start/Restart always announces the (re)start and re-arms that
//    card's warning, instead of depending on the timing race between the
//    starttime change and the next 1Hz clock tick
export default function useTimerSound({
  enabled,
  warningLeadSeconds,
  isEmpty,
  isOver,
  activeCard,
  starttime,
}) {
  useAudioUnlock();

  const hasSeenTick = useRef(false);
  const lastActiveIndexRef = useRef(null);
  const lastIsOverRef = useRef(false);
  const warnedIndexRef = useRef(null);
  const lastStarttimeRef = useRef(null);

  useEffect(() => {
    const starttimeKey = starttime ? starttime.valueOf() : null;

    if (isEmpty) {
      hasSeenTick.current = false;
      lastActiveIndexRef.current = null;
      lastIsOverRef.current = false;
      warnedIndexRef.current = null;
      lastStarttimeRef.current = starttimeKey;
      return;
    }

    const activeIndex = activeCard ? activeCard.index : null;

    if (!hasSeenTick.current) {
      hasSeenTick.current = true;
      lastActiveIndexRef.current = activeIndex;
      lastIsOverRef.current = isOver;
      lastStarttimeRef.current = starttimeKey;
      return;
    }

    if (starttimeKey !== lastStarttimeRef.current) {
      lastActiveIndexRef.current = null;
      lastIsOverRef.current = false;
      warnedIndexRef.current = null;
      lastStarttimeRef.current = starttimeKey;
    }

    if (enabled) {
      if (isOver && !lastIsOverRef.current) {
        playSound(changeSound);
      } else if (activeIndex !== null && activeIndex !== lastActiveIndexRef.current) {
        playSound(changeSound);
      } else if (
        activeCard &&
        activeCard.remainingSeconds > 0 &&
        warnedIndexRef.current !== activeIndex
      ) {
        const effectiveLeadSeconds = Math.min(
          warningLeadSeconds,
          activeCard.durationSeconds / 2
        );
        if (activeCard.remainingSeconds <= effectiveLeadSeconds) {
          playSound(countdownSound);
          warnedIndexRef.current = activeIndex;
        }
      }
    }

    lastActiveIndexRef.current = activeIndex;
    lastIsOverRef.current = isOver;
  });
}
